import Layout from "@/components/Layout";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import PermissionDenied from "@/components/PermissionDenied";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import {
  closeEmployeeLoan,
  createEmployeeLoan,
  listAllEmployeeLoans,
  listLoanInstallments,
  loansApiErrorMessage,
  skipLoanInstallment,
  updateEmployeeLoan,
  type EmployeeLoanRecord,
  type LoanStatus,
} from "@/lib/hr/loansStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { format } from "date-fns";
import {
  Banknote,
  CalendarIcon,
  Check,
  ChevronsUpDown,
  Edit,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Receipt,
  Search,
  User,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type Draft = {
  employeeId: string;
  loanAmount: string;
  installmentAmount: string;
  startDate: string;
};

const emptyDraft = (): Draft => ({
  employeeId: "",
  loanAmount: "",
  installmentAmount: "",
  startDate: format(new Date(), "yyyy-MM-dd"),
});

function loanTitle(r: EmployeeLoanRecord): string {
  return r.employeeName ? `${r.employeeName}` : `Loan · ${r.loanAmount}`;
}

function statusBadge(status: LoanStatus) {
  if (status === "completed") return <Badge variant="secondary">Completed</Badge>;
  return <Badge>Active</Badge>;
}

function safeNumber(v: string): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ── employee helpers (same as AttendanceDailyPanel) ── */

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeeLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

type LoanSortBy = "latest" | "oldest" | "amount_desc" | "amount_asc";

export default function EmployeeLoans() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "employee_loan_can_create");
  const canUpdate = can(user, "employee_loan_can_update");
  const canClose = can(user, "employee_loan_can_close");
  const canViewInstallments = can(user, "employee_loan_can_view_installments");
  const canSkipInstallment = can(user, "employee_loan_can_skip_installment");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [rows, setRows] = useState<EmployeeLoanRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<"all" | LoanStatus>("all");
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<LoanSortBy>("latest");

  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [filterEmployeePickerOpen, setFilterEmployeePickerOpen] = useState(false);
  const [filterEmployeePickerCycle, setFilterEmployeePickerCycle] = useState(0);

  const selectedFilterEmployee = useMemo(
    () => (filterEmployeeId ? employees.find((e) => e.id === filterEmployeeId) : undefined),
    [filterEmployeeId, employees],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeLoanRecord | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const [closingTarget, setClosingTarget] = useState<EmployeeLoanRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const [installmentsOpen, setInstallmentsOpen] = useState(false);
  const [installmentsLoan, setInstallmentsLoan] = useState<EmployeeLoanRecord | null>(null);
  const [installments, setInstallments] = useState<
    { id: string; salaryId: string | null; period: string; amount: string; deductedOn: string | null; status: string; note: string | null }[]
  >([]);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);

  const [skipOpen, setSkipOpen] = useState(false);
  const [skipLoan, setSkipLoan] = useState<EmployeeLoanRecord | null>(null);
  const [skipMonth, setSkipMonth] = useState(() => format(new Date(), "yyyy-MM"));
  const [skipNote, setSkipNote] = useState("");
  const [skipping, setSkipping] = useState(false);
  const [skipPickerOpen, setSkipPickerOpen] = useState(false);
  const [skipPickerYear, setSkipPickerYear] = useState(() => new Date().getFullYear());

  const monthOptions = useMemo(
    () => [
      { v: 1, label: "Jan" },
      { v: 2, label: "Feb" },
      { v: 3, label: "Mar" },
      { v: 4, label: "Apr" },
      { v: 5, label: "May" },
      { v: 6, label: "Jun" },
      { v: 7, label: "Jul" },
      { v: 8, label: "Aug" },
      { v: 9, label: "Sep" },
      { v: 10, label: "Oct" },
      { v: 11, label: "Nov" },
      { v: 12, label: "Dec" },
    ],
    [],
  );

  const skipMonthLabel = useMemo(() => {
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) return "Pick month";
    const [yy, mm] = m.split("-").map((x) => Number(x));
    const label = monthOptions.find((x) => x.v === mm)?.label ?? String(mm);
    return `${label} ${yy}`;
  }, [skipMonth, monthOptions]);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[0]);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listAllEmployeeLoans({
        page,
        perPage: pageSize,
        status: filterStatus,
        employeeId: filterEmployeeId || undefined,
      });
      setRows(res.rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(loansApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterStatus, filterEmployeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterStartDate) {
        const want = format(filterStartDate, "yyyy-MM-dd");
        if (r.startDate !== want) return false;
      }
      if (!q) return true;
      const hay = `${r.employeeName} ${r.loanAmount} ${r.installmentAmount} ${r.remainingAmount} ${r.totalInstallments} ${r.startDate} ${r.status}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query, filterStartDate]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "amount_desc":
          return parseFloat(b.loanAmount) - parseFloat(a.loanAmount);
        case "amount_asc":
          return parseFloat(a.loanAmount) - parseFloat(b.loanAmount);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterStatus !== "all" ? 1 : 0) + (filterStartDate ? 1 : 0) + (filterEmployeeId ? 1 : 0);
  const resetFilters = () => {
    setFilterStatus("all");
    setFilterStartDate(undefined);
    setFilterEmployeeId("");
  };

  const paginationKey = `${query}|${filterStatus}|${filterStartDate?.getTime() ?? ""}|${filterEmployeeId}|${pageSize}|${sortBy}`;
  const { paginatedItems, totalCount, totalPages, rangeStart, rangeEnd } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyDraft());
    setModalOpen(true);
  };

  const openEdit = (r: EmployeeLoanRecord) => {
    setEditing(r);
    setDraft({
      employeeId: r.employeeId,
      loanAmount: r.loanAmount,
      installmentAmount: r.installmentAmount,
      startDate: r.startDate,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDraft(emptyDraft());
  };

  const previewInstallments = useMemo(() => {
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || !inst || loan <= 0 || inst <= 0) return null;
    return Math.max(1, Math.ceil(loan / inst));
  }, [draft.loanAmount, draft.installmentAmount]);

  const save = async () => {
    if (saving) return;
    if (!draft.employeeId) return toast.error("Select an employee.");
    const loan = safeNumber(draft.loanAmount);
    const inst = safeNumber(draft.installmentAmount);
    if (!loan || loan <= 0) return toast.error("Loan amount must be a number > 0.");
    if (!inst || inst <= 0) return toast.error("Installment amount must be a number > 0.");
    if (!draft.startDate.trim()) return toast.error("Start date is required.");

    setSaving(true);
    try {
      if (editing) {
        const result = await updateEmployeeLoan({
          employeeId: draft.employeeId,
          id: editing.id,
          patch: { installmentAmount: draft.installmentAmount, startDate: draft.startDate },
        });
        toast.success(result.message);
      } else {
        const result = await createEmployeeLoan({
          employeeId: draft.employeeId,
          loanAmount: draft.loanAmount,
          installmentAmount: draft.installmentAmount,
          startDate: draft.startDate,
        });
        toast.success(result.message);
      }
      closeModal();
      setPage(1);
      await load();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmClose = async () => {
    if (!closingTarget || closing) return;
    setClosing(true);
    try {
      const result = await closeEmployeeLoan({ employeeId: closingTarget.employeeId, id: closingTarget.id });
      toast.success(result.message);
      setClosingTarget(null);
      setPage(1);
      await load();
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setClosing(false);
    }
  };

  const openInstallments = useCallback(async (loan: EmployeeLoanRecord) => {
    setInstallmentsLoan(loan);
    setInstallmentsOpen(true);
    setInstallmentsLoading(true);
    try {
      const res = await listLoanInstallments({ employeeId: loan.employeeId, loanId: loan.id, perPage: 50, page: 1 });
      setInstallments(
        res.rows.map((x) => ({
          id: x.id,
          salaryId: x.salaryId,
          period: x.periodYear && x.periodMonth ? `${x.periodYear}-${String(x.periodMonth).padStart(2, "0")}` : "—",
          amount: x.installmentAmount,
          deductedOn: x.deductedOn,
          status: x.status,
          note: x.note ?? null,
        })),
      );
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
      setInstallments([]);
    } finally {
      setInstallmentsLoading(false);
    }
  }, []);

  const openSkip = (loan: EmployeeLoanRecord) => {
    setSkipLoan(loan);
    setSkipMonth(format(new Date(), "yyyy-MM"));
    setSkipPickerYear(new Date().getFullYear());
    setSkipNote("");
    setSkipOpen(true);
  };

  const submitSkip = async () => {
    if (!skipLoan || skipping) return;
    const m = skipMonth.trim();
    if (!/^\d{4}-\d{2}$/.test(m)) {
      toast.error("Pick a month (YYYY-MM).");
      return;
    }
    const [yy, mm] = m.split("-").map((x) => Number(x));
    if (!yy || !mm) {
      toast.error("Pick a valid month.");
      return;
    }
    setSkipping(true);
    try {
      const result = await skipLoanInstallment({
        employeeId: skipLoan.employeeId,
        loanId: skipLoan.id,
        periodYear: yy,
        periodMonth: mm,
        note: skipNote,
      });
      toast.success(result.message);
      setSkipOpen(false);
      if (installmentsOpen && installmentsLoan?.id === skipLoan.id) {
        await openInstallments(skipLoan);
      }
    } catch (e) {
      toast.error(loansApiErrorMessage(e));
    } finally {
      setSkipping(false);
    }
  };

  /* ── selected employee in modal ── */
  const selectedModalEmployee = useMemo(
    () => (draft.employeeId ? employees.find((e) => e.id === draft.employeeId) : undefined),
    [draft.employeeId, employees],
  );

  return isForbidden ? (
    <PermissionDenied
      title="Employee loans access required"
      message="You don't have permission to view employee loans. Ask an administrator to grant employee_loan_can_view_any."
    />
  ) : (
    <Layout title="Employee loans">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Employee loans</h1>
            <p className="text-sm text-muted-foreground mt-1">Create loans and auto-deduct installments during salary processing.</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              Add loan
            </Button>
          ) : null}
        </div>

        {/* ── Create / Edit Modal ── */}
        <Dialog
          open={modalOpen}
          onOpenChange={(o) => {
            if (!o) closeModal();
            else setModalOpen(true);
          }}
        >
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit loan" : "New loan"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-4 py-2">
              {/* Employee combobox */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Employee <span className="text-destructive">*</span>
                </Label>
                {editing ? (
                  /* locked when editing */
                  <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-border bg-muted/30">
                    {selectedModalEmployee ? (
                      <>
                        <Avatar className="h-6 w-6 text-[9px]">
                          {selectedModalEmployee.photoDataUrl ? (
                            <AvatarImage src={selectedModalEmployee.photoDataUrl} alt={employeeDisplayName(selectedModalEmployee)} />
                          ) : null}
                          <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                            {employeeInitials(selectedModalEmployee)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm truncate">{employeeLabel(selectedModalEmployee)}</span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">{editing.employeeName || "Employee"}</span>
                    )}
                  </div>
                ) : (
                  <Popover
                    modal
                    open={employeePickerOpen}
                    onOpenChange={(next) => {
                      setEmployeePickerOpen(next);
                      if (next) setEmployeePickerCycle((c) => c + 1);
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={employeePickerOpen}
                        className={cn(
                          "glass-input h-10 w-full justify-between px-3 font-normal",
                          !draft.employeeId && "text-muted-foreground",
                        )}
                      >
                        {selectedModalEmployee ? (
                          <span className="flex items-center gap-2 truncate">
                            <Avatar className="h-5 w-5 text-[8px]">
                              {selectedModalEmployee.photoDataUrl ? (
                                <AvatarImage src={selectedModalEmployee.photoDataUrl} alt={employeeDisplayName(selectedModalEmployee)} />
                              ) : null}
                              <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                {employeeInitials(selectedModalEmployee)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="truncate">{employeeLabel(selectedModalEmployee)}</span>
                          </span>
                        ) : (
                          <span className="truncate text-left">Select employee…</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                      <Command key={employeePickerCycle} shouldFilter>
                        <CommandInput placeholder="Search name, code or department…" className="h-9" />
                        <CommandList>
                          <CommandEmpty>No employee found.</CommandEmpty>
                          <CommandGroup>
                            {activeEmployees.map((e) => {
                              const label = employeeLabel(e);
                              const subtitle = employeeSubtitle(e);
                              const selected = draft.employeeId === e.id;
                              const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                                .filter(Boolean)
                                .join(" ");
                              return (
                                <CommandItem
                                  key={e.id}
                                  value={searchValue}
                                  onSelect={() => {
                                    setDraft((d) => ({ ...d, employeeId: e.id }));
                                    setEmployeePickerOpen(false);
                                  }}
                                  className="gap-2"
                                >
                                  <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                  <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                    {e.photoDataUrl ? (
                                      <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                    ) : null}
                                    <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                      {employeeInitials(e)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex flex-col min-w-0">
                                    <span className="truncate text-sm font-medium">{label}</span>
                                    {subtitle && (
                                      <span className="truncate text-[11px] opacity-60 leading-tight">
                                        {subtitle}
                                      </span>
                                    )}
                                  </div>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Loan amount</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.loanAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, loanAmount: e.target.value }))}
                  placeholder="e.g. 50000"
                  inputMode="decimal"
                  disabled={!!editing}
                />
                {editing ? <p className="text-xs text-muted-foreground">Loan amount is fixed after creation.</p> : null}
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Installment per month</Label>
                <Input
                  className="glass-input h-10"
                  value={draft.installmentAmount}
                  onChange={(e) => setDraft((d) => ({ ...d, installmentAmount: e.target.value }))}
                  placeholder="e.g. 5000"
                  inputMode="decimal"
                />
                {previewInstallments ? (
                  <p className="text-xs text-muted-foreground">Estimated total installments: {previewInstallments}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Total installments will be calculated automatically.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Start date <span className="text-destructive">*</span>
                </Label>
                <DatePartsPicker
                  value={draft.startDate}
                  onChange={(iso) => setDraft((x) => ({ ...x, startDate: iso }))}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 5}
                  placeholder="Select start date"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Installments Dialog ── */}
        <Dialog open={installmentsOpen} onOpenChange={(o) => (!o ? setInstallmentsOpen(false) : setInstallmentsOpen(true))}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Installments</DialogTitle>
            </DialogHeader>
            {installmentsLoan ? (
              <div className="text-sm text-muted-foreground">
                {installmentsLoan.employeeName ? (
                  <span className="text-foreground font-semibold">{installmentsLoan.employeeName}</span>
                ) : null}
                {installmentsLoan.employeeName ? " · " : ""}
                Loan amount <span className="text-foreground font-semibold">{installmentsLoan.loanAmount}</span> · Remaining{" "}
                <span className="text-foreground font-semibold">{installmentsLoan.remainingAmount}</span>
              </div>
            ) : null}
            {installmentsLoading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : installments.length === 0 ? (
              <div className="text-sm text-muted-foreground">No installments found yet. They will appear after salary processing.</div>
            ) : (
              <div className="divide-y rounded-xl border border-border/50">
                {installments.map((x) => (
                  <div key={x.id} className="p-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-muted border border-border/50 flex items-center justify-center shrink-0">
                      <Receipt className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground">Amount: {x.amount}</div>
                      <div className="text-xs text-muted-foreground">
                        Period: {x.period} · Deducted on: {x.deductedOn ?? "—"} · Salary ID: {x.salaryId ?? "—"}
                      </div>
                      {x.note ? <div className="text-xs text-muted-foreground mt-1 truncate">Note: {x.note}</div> : null}
                    </div>
                    <Badge
                      variant={x.status === "paid" ? "secondary" : x.status === "pending" ? "default" : "outline"}
                    >
                      {x.status === "pending" ? "pending payroll" : x.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ── Skip Installment Dialog ── */}
        <Dialog open={skipOpen} onOpenChange={setSkipOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Skip installment for a month</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-1">
              {skipLoan ? (
                <div className="text-xs text-muted-foreground">
                  Loan <span className="text-foreground font-semibold">{skipLoan.loanAmount}</span> · Remaining{" "}
                  <span className="text-foreground font-semibold">{skipLoan.remainingAmount}</span>
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label>Month</Label>
                <Popover modal={true} open={skipPickerOpen} onOpenChange={setSkipPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className={cn("glass-input h-10 w-full justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4 opacity-70 shrink-0" />
                      {skipMonthLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-4 z-[100]" align="start" sideOffset={6}>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y - 1)}
                        aria-label="Previous year"
                      >
                        ‹
                      </Button>
                      <div className="flex-1">
                        <Select value={String(skipPickerYear)} onValueChange={(v) => setSkipPickerYear(Number(v))}>
                          <SelectTrigger className="glass-input h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 7 }).map((_, i) => {
                              const y = new Date().getFullYear() - 3 + i;
                              return (
                                <SelectItem key={y} value={String(y)}>
                                  {y}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setSkipPickerYear((y) => y + 1)}
                        aria-label="Next year"
                      >
                        ›
                      </Button>
                    </div>

                    <div className="mt-4 grid grid-cols-4 gap-2">
                      {monthOptions.map((m) => {
                        const iso = `${skipPickerYear}-${String(m.v).padStart(2, "0")}`;
                        const active = iso === skipMonth;
                        return (
                          <Button
                            key={m.v}
                            type="button"
                            variant={active ? "premium" : "outline"}
                            className={cn("h-9", !active && "glass-input")}
                            onClick={() => {
                              setSkipMonth(iso);
                              setSkipPickerOpen(false);
                            }}
                          >
                            {m.label}
                          </Button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Input className="glass-input h-10" value={skipNote} onChange={(e) => setSkipNote(e.target.value)} placeholder="e.g. Employee requested skip" />
              </div>
              <div className="text-xs text-muted-foreground">
                Salary processing for that month will <strong className="text-foreground">not deduct</strong> this loan installment.
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setSkipOpen(false)} disabled={skipping}>
                Cancel
              </Button>
              <Button variant="premium" className="gap-2" onClick={() => void submitSkip()} disabled={skipping}>
                {skipping ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Skip month
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Search + Sheet Filters ── */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search employee, amount, status…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter loans</SheetTitle>
                  <SheetDescription>Status and start date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Loan</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Employee</Label>
                      <Popover
                        modal
                        open={filterEmployeePickerOpen}
                        onOpenChange={(next) => {
                          setFilterEmployeePickerOpen(next);
                          if (next) setFilterEmployeePickerCycle((c) => c + 1);
                        }}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            role="combobox"
                            aria-expanded={filterEmployeePickerOpen}
                            className={cn(
                              "glass-input h-10 w-full justify-between px-3 font-normal",
                              !filterEmployeeId && "text-muted-foreground",
                            )}
                          >
                            {selectedFilterEmployee ? (
                              <span className="flex items-center gap-2 truncate">
                                <Avatar className="h-5 w-5 text-[8px]">
                                  {selectedFilterEmployee.photoDataUrl ? (
                                    <AvatarImage src={selectedFilterEmployee.photoDataUrl} alt={employeeDisplayName(selectedFilterEmployee)} />
                                  ) : null}
                                  <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                    {employeeInitials(selectedFilterEmployee)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="truncate">{employeeLabel(selectedFilterEmployee)}</span>
                              </span>
                            ) : (
                              <span className="truncate text-left">All employees</span>
                            )}
                            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                          <Command key={filterEmployeePickerCycle} shouldFilter>
                            <CommandInput placeholder="Search name, code or department…" className="h-9" />
                            <CommandList>
                              <CommandEmpty>No employee found.</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  value="__all__ all employees"
                                  onSelect={() => {
                                    setFilterEmployeeId("");
                                    setFilterEmployeePickerOpen(false);
                                  }}
                                  className="gap-2"
                                >
                                  <Check className={cn("h-3.5 w-3.5 shrink-0", !filterEmployeeId ? "opacity-100" : "opacity-0")} />
                                  <User className="h-4 w-4 text-muted-foreground" />
                                  <span>All employees</span>
                                </CommandItem>
                                {activeEmployees.map((e) => {
                                  const label = employeeLabel(e);
                                  const subtitle = employeeSubtitle(e);
                                  const selected = filterEmployeeId === e.id;
                                  const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                                    .filter(Boolean)
                                    .join(" ");
                                  return (
                                    <CommandItem
                                      key={e.id}
                                      value={searchValue}
                                      onSelect={() => {
                                        setFilterEmployeeId(e.id);
                                        setFilterEmployeePickerOpen(false);
                                      }}
                                      className="gap-2"
                                    >
                                      <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                      <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                        {e.photoDataUrl ? (
                                          <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                        ) : null}
                                        <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                          {employeeInitials(e)}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="flex flex-col min-w-0">
                                        <span className="truncate text-sm font-medium">{label}</span>
                                        {subtitle && (
                                          <span className="truncate text-[11px] opacity-60 leading-tight">
                                            {subtitle}
                                          </span>
                                        )}
                                      </div>
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as never)}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Start date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button type="button" variant="outline" className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterStartDate && "text-muted-foreground")}>
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterStartDate ? format(filterStartDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterStartDate} onSelect={setFilterStartDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterStartDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          {/* ── Controls bar: per-page, sort, grid/list ── */}
          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect id="loan-page-size-desktop" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as LoanSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Amount: High to Low</SelectItem>
                    <SelectItem value="amount_asc">Amount: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* mobile controls */}
          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="loan-page-size-mobile" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as never)} options={PAGE_SIZE_OPTIONS} />
            <div className="flex items-center gap-2">
              <div className="w-[170px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as LoanSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="amount_desc">Amount: High to Low</SelectItem>
                    <SelectItem value="amount_asc">Amount: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading loans…
          </div>
        ) : null}

        {!loading && sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0 ? 'No loans yet. Click "Add loan" to create one.' : "No loans match your search."}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginatedItems.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  className="h-full flex flex-col"
                  title={loanTitle(r)}
                  titleIcon={User}
                  statusBadge={
                    r.status === "completed"
                      ? { label: "Completed", className: "bg-muted text-muted-foreground" }
                      : { label: "Active", className: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" }
                  }
                  primaryMeta={{ label: "Remaining", value: r.remainingAmount, icon: Banknote, iconClassName: "text-emerald-600" }}
                  secondaryMeta={{ label: "Start", value: r.startDate, icon: CalendarIcon, iconClassName: "text-sky-600" }}
                  footer={`Loan: ${r.loanAmount} · Installment: ${r.installmentAmount} · Updated ${formatCatalogAuditDate(r.updatedAt)}`}
                  onEdit={canUpdate ? () => openEdit(r) : undefined}
                  onDelete={canClose && r.status === "active" ? () => setClosingTarget(r) : undefined}
                  extraActions={
                    <>
                      {canSkipInstallment && r.status === "active" ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 gap-1.5 h-9 px-3 text-xs font-medium"
                          onClick={() => openSkip(r)}
                        >
                          Skip
                        </Button>
                      ) : null}
                      {canViewInstallments ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 gap-1.5 h-9 px-3 text-xs font-medium"
                          onClick={() => void openInstallments(r)}
                        >
                          <Receipt className="w-3.5 h-3.5 shrink-0" />
                          Installments
                        </Button>
                      ) : null}
                    </>
                  }
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {!loading && sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginatedItems.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className="glass-card p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-14 w-16 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Banknote className="w-7 h-7 text-emerald-500/70" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">
                      {r.employeeName || "—"} · loan {r.loanAmount}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                      remaining {r.remainingAmount} · installment {r.installmentAmount} · start {r.startDate}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canViewInstallments ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => void openInstallments(r)}>
                        <Receipt className="w-3 h-3" /> Installments
                      </Button>
                    ) : null}
                    {canSkipInstallment ? (
                      <Button
                        variant="glass"
                        size="sm"
                        className="gap-1.5 h-8 text-xs"
                        disabled={r.status !== "active"}
                        onClick={() => openSkip(r)}
                      >
                        Skip month
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => openEdit(r)}>
                        <Edit className="w-3 h-3" /> Edit
                      </Button>
                    ) : null}
                    {canClose ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 px-3 text-xs"
                        disabled={r.status !== "active"}
                        onClick={() => setClosingTarget(r)}
                      >
                        Close
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {!loading && sorted.length > 0 ? (
          <GridPaginationFooter totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} page={page} totalPages={totalPages} onPageChange={setPage} />
        ) : null}

        <ConfirmAlertDialog
          open={!!closingTarget}
          onOpenChange={(o) => !o && setClosingTarget(null)}
          title="Close this loan?"
          description={
            closingTarget ? (
              <>
                Close <strong className="text-foreground font-semibold">{closingTarget.loanAmount}</strong> with remaining{" "}
                <strong className="text-foreground font-semibold">{closingTarget.remainingAmount}</strong>?
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel={closing ? "Closing..." : "Close"}
          cancelLabel="Cancel"
          onConfirm={() => void confirmClose()}
        />
      </div>
    </Layout>
  );
}
